import React from 'react';
import GithubRepoTopicEditor from './GithubRepoTopicEditor';
import GitRepoCheckboxes from './GitRepoCheckboxes';
import GitRepoListContainer from './GitRepoListContainer';
import { FieldSet } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { GitRepoPlatform } from '../../graphql/generated/graphql';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';

const PLATFORM_PERMISSION_MODEL_NOTE: Record<string, string> = {
  github: 'Only organization repos with admin permission are supported.',
  gitlab:
    'Only active repos with maintainer permission or above are supported.',
};

const INTEGRATION_FRAGMENT = gql(`
  fragment RepoSyncTab_integration on Integration {
    id
    platform
    settings {
      syncingRepos {
        name
        topicId
      }
    }
  }
`);

const getGitPlatform = (platform: string) => {
  switch (platform) {
    case 'github':
      return GitRepoPlatform.Github;
    case 'gitlab':
      return GitRepoPlatform.Gitlab;
    default:
      return null;
  }
};

interface RepoSyncTabProps {
  integration: DocumentType<typeof INTEGRATION_FRAGMENT>;
  platformInfo: IntegrationPlatformInfo;
}

const RepoSyncTab = ({
  integration: { platform },
  platformInfo,
}: RepoSyncTabProps) => {
  const gitPlatform = getGitPlatform(platform);

  return (
    <FieldSet>
      <GitRepoListContainer footnote={PLATFORM_PERMISSION_MODEL_NOTE[platform]}>
        {/* TODO: merge the two views */}
        {platformInfo.repoSync?.topicMapping && (
          <GithubRepoTopicEditor name="repos" />
        )}
        {!platformInfo.repoSync?.topicMapping && !!gitPlatform && (
          <GitRepoCheckboxes name="repos" platform={gitPlatform} />
        )}
      </GitRepoListContainer>
    </FieldSet>
  );
};

RepoSyncTab.getFormValues = ({
  settings,
}: DocumentType<typeof INTEGRATION_FRAGMENT>) => ({
  repos: (settings?.syncingRepos || []).map(({ name }) => name),
  topicIdMap: (settings?.syncingRepos || []).reduce<
    Record<string, string | null>
  >(
    (memo, { name, topicId }) => ({
      ...memo,
      [name]: topicId ?? null,
    }),
    {},
  ),
});
RepoSyncTab.getNewSettings = (
  values: ReturnType<typeof RepoSyncTab.getFormValues>,
  { user }: { user: { id: string } },
) => ({
  importOwnerId: user.id,
  syncingRepos: values.repos.map((name) => ({
    name,
    topicId: values.topicIdMap[name],
  })),
});

export default RepoSyncTab;
